import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Brain, 
  Cloud, 
  Shield, 
  BarChart3, 
  Cog, 
  Rocket,
  ArrowRight 
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ExpertiseSection = () => {
  const services = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "FinTech Strategy",
      description: "Develop comprehensive architecture roadmaps aligned with regulatory requirements and business growth.",
      features: ["Architecture Planning", "Technology Assessment", "Digital Roadmaps", "Innovation Strategy"],
      color: "text-blue-500"
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Distributed Systems",
      description: "Design and implement scalable microservices solutions for enhanced performance and resilience.",
      features: ["Microservices", "Event-Driven Arch", "High Availability", "Infrastructure as Code"],
      color: "text-green-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Security & Compliance",
      description: "Protect financial assets with comprehensive KYC/AML frameworks and native risk management.",
      features: ["Zero-Trust Security", "AML/KYC Integration", "Regulatory Audits", "Incident Response"],
      color: "text-red-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "High-Throughput Processing",
      description: "Transform core banking systems to process thousands of transactions securely per second.",
      features: ["Message Brokers", "Data Pipelines", "Performance Logs", "Transaction Ledgers"],
      color: "text-purple-500"
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: "Process Modernization",
      description: "Streamline engineering operations and build strong developer cultures and CI/CD workflows.",
      features: ["DevOps", "Automation", "CI/CD Workflows", "Platform Engineering"],
      color: "text-orange-500"
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Engineering Leadership",
      description: "Build, scale, and lead remote cross-functional engineering teams to deliver world-class products.",
      features: ["Team Scaling", "Agile Integration", "Cultural Leadership", "Mentorship"],
      color: "text-indigo-500"
    }
  ];

  return (
    <section id="expertise" className="py-20 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4">Expertise</Badge>
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            <span className="text-primary">Capabilities</span> That Drive Scale
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Comprehensive technology consulting services designed to accelerate your digital transformation 
            journey and unlock new opportunities for growth.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <Card 
              key={service.title}
              className="card-elegant group hover:glow-effect transition-all duration-500 cursor-pointer animate-fade-in-scale"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardHeader className="pb-4">
                <div className="mb-4">
                  <div className={`inline-flex p-3 bg-muted rounded-lg ${service.color} group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                </div>
                <CardTitle className="text-xl group-hover:gradient-text transition-all duration-300">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  {service.description}
                </p>
                <div className="space-y-2">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center text-sm">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3"></div>
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full group/btn mt-4 hover:bg-primary/10"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Discuss This
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-20 lg:mt-32 relative">
          {/* Ambient Background Glow */}
          <div className="absolute inset-0 w-3/4 mx-auto bg-primary/10 blur-[120px] rounded-[100%] pointer-events-none"></div>

          <Card className="relative overflow-hidden border border-primary/20 bg-[#0A0F15]/90 backdrop-blur-2xl max-w-5xl mx-auto shadow-2xl">
            {/* Abstract Tech Patterns inside CTA */}
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none transition-all duration-1000"></div>
            
            <div className="relative p-10 md:p-16 flex flex-col lg:flex-row items-center lg:items-start text-center lg:text-left justify-between gap-12">
              <div className="max-w-2xl">
                <Badge variant="outline" className="mb-6 px-4 py-1.5 border-primary/50 text-primary bg-primary/5 shadow-glow">
                   Executive Consulting
                </Badge>
                <h3 className="text-3xl lg:text-4xl font-bold mb-6 tracking-tight text-foreground">
                  Ready to scale your <span className="text-primary italic">engineering</span> organization?
                </h3>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Whether you are transitioning from monolithic systems, securing your FinTech infrastructure, or optimizing engineering velocity, let's architect a solution that drives exponential growth.
                </p>
              </div>

              <div className="flex flex-col w-full sm:w-auto gap-4 shrink-0 lg:mt-4">
                <Button 
                  size="lg" 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,255,255,0.2)] hover:shadow-[0_0_40px_rgba(0,255,255,0.4)] transition-all px-8 py-6 text-lg rounded-xl font-bold"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-primary/30 text-primary hover:bg-primary/10 px-8 py-6 text-lg rounded-xl transition-all"
                  onClick={() => document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  View Past Experience
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;